/**
 * Redaction for anything captured off the page.
 *
 * The portal renders guest names, lead email addresses and phone numbers, and
 * hotel records. None of that belongs in a telemetry table, and autocapture by
 * its nature reads whatever happens to be on screen — so every captured string
 * passes through here, and the capture layer above it is additionally
 * restricted in what it will read at all (see `describeElement`).
 */

/** Longest label we keep. Long enough for any real control, short enough to be a label. */
const MAX_LABEL_LENGTH = 40;

/** Longest route path we keep. */
const MAX_PATH_LENGTH = 500;

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Seven or more digits, ignoring the spaces, dashes, dots and parens people
 * write phone numbers with. Shorter runs are left alone so "2026" or an item
 * count survives — those carry meaning and no identity.
 *
 * The leading `[(+]?` matters: without it the match starts at the first digit
 * and leaves the opening bracket of "(617) 555-0100" stranded next to the
 * placeholder.
 */
const LONG_NUMBER_PATTERN = /[(+]?(?:\d[\s()+.-]*){6,}\d/g;

const REDACTED_EMAIL = '[email]';
const REDACTED_ID = '[id]';
const REDACTED_NUMBER = '[number]';

/**
 * Strip identifying substrings and normalise whitespace.
 *
 * Order matters: emails go first, because an address containing digits would
 * otherwise be half-eaten by the number rule and land in the table as a
 * recognisable fragment.
 *
 * @param value - Raw text read from the page.
 * @returns Redacted, whitespace-collapsed, length-capped text.
 */
export function redactLabel(value: string): string {
  return value
    .replace(EMAIL_PATTERN, REDACTED_EMAIL)
    .replace(UUID_PATTERN, REDACTED_ID)
    .replace(LONG_NUMBER_PATTERN, REDACTED_NUMBER)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_LABEL_LENGTH);
}

/**
 * Reduce a URL to the part worth analysing.
 *
 * Query strings and fragments are dropped wholesale rather than redacted: they
 * carry search terms and filter values typed by the user, and no question this
 * data answers needs them. Path segments are kept — the record ids in them are
 * our own UUIDs, which are what make a click joinable to the thing clicked.
 *
 * @param url - A full URL or a path.
 * @returns The pathname, length-capped.
 */
export function redactPath(url: string): string {
  const withoutFragment = url.split('#')[0] ?? '';
  const withoutQuery = withoutFragment.split('?')[0] ?? '';
  return withoutQuery.slice(0, MAX_PATH_LENGTH);
}
